import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { forkJoin, merge, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import {
  CategoryValueDto,
  DashboardFilters,
  GeoVolumeDto,
  PublisherDashboardApiService,
  SociodemoRowDto,
  TncidsTrendsDto,
  XyPointDto
} from '../../services/publisher-dashboard-api.service';
import { ProviderDto, ProvidersApiService } from '../../services/providers-api.service';
import { CustomSelectComponent, CustomSelectOption } from '../shared/custom-select/custom-select.component';
import { AuthService } from '../../services/auth.service';

Chart.register(...registerables);

const CATEGORY_COLORS = [
  '#1e3a8a', '#3b82f6', '#fbbf24', '#f97316', '#8b5cf6',
  '#ec4899', '#10b981', '#14b8a6', '#f59e0b', '#ef4444'
];

// Light → dark shades of the same blue hue, used to color treemap columns by rank
// rather than by category identity.
const MONO_BLUE_SHADES = [
  '#3b82f6', '#3572da', '#2e63c2', '#2754aa', '#204593', '#1e3a8a'
];

const TIME_RANGE_OPTIONS = [7, 14, 30, 60, 90];

type GenderView = 'total' | 'male' | 'female';

interface StatCard {
  key: string;
  label: string;
  value: string;
  icon: string;
}

interface AgeBreakdown {
  label: string;
  count: number;
  percent: number;
}

interface GeoRow {
  code: string;
  name: string;
  value: number;
  pct: number;
}

interface RankedRow {
  label: string;
  value: number;
  pct: number;
  color: string;
}

interface TagCloudWord {
  label: string;
  value: number;
  pct: number;
  fontSize: number;
  color: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('trendsCanvas') trendsCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('externalIdsCanvas') externalIdsCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('ageDonutCanvas') ageDonutCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('ageBarCanvas') ageBarCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('browserPieCanvas') browserPieCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('devicePieCanvas') devicePieCanvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('osPieCanvas') osPieCanvasRef?: ElementRef<HTMLCanvasElement>;

  readonly timeRangeOptions = TIME_RANGE_OPTIONS;
  selectedTimeRange = 30;

  // Provider filter — scoped to the currently selected entity. '' means "all providers"
  // (server aggregates across the entity). Shown only when the entity has more than one.
  selectedProviderId = '';
  providerOptions: CustomSelectOption[] = [];

  loadingOverview = true;
  overviewError = false;
  statCards: StatCard[] = [];
  trendsHasData = true;
  externalIdsHasData = true;

  loadingAdvanced = false;
  advancedError = false;

  geoRows: GeoRow[] = [];
  geoHasData = true;

  ageGenderHasData = true;
  selectedGenderView: GenderView = 'total';
  malePercent = 0;
  femalePercent = 0;
  private maleBreakdown: AgeBreakdown[] = [];
  private femaleBreakdown: AgeBreakdown[] = [];
  private totalBreakdown: AgeBreakdown[] = [];

  providerRows: RankedRow[] = [];
  providerHasData = true;

  tagWords: TagCloudWord[] = [];
  tagsHasData = true;

  browserHasData = true;
  deviceHasData = true;
  osHasData = true;

  private trendsChart: Chart | null = null;
  private externalIdsChart: Chart | null = null;
  private ageDonutChart: Chart | null = null;
  private ageBarChart: Chart | null = null;
  private browserPieChart: Chart | null = null;
  private devicePieChart: Chart | null = null;
  private osPieChart: Chart | null = null;

  // Per-card data date (YYYY-MM-DD) when it is older than yesterday — the nightly ETL should
  // always leave yesterday's data. Absent key = data fresh (or date unknown) → no warning.
  staleDates: Record<string, string> = {};

  private goalUsersCount = 0;
  private readonly locale = window?.navigator?.language || 'en-US';
  private readonly regionNames: { of(code: string): string } | null = this.createRegionDisplayNames();

  private readonly subscriptions = new Subscription();
  private providersSub: Subscription | null = null;

  constructor(
    private api: PublisherDashboardApiService,
    private translate: TranslateService,
    private authService: AuthService,
    private providersApi: ProvidersApiService
  ) {}

  ngOnInit(): void {
    // Reload when the active entity changes (including switching to "Admin") OR when the
    // impersonated user changes identity. debounceTime(0) coalesces the two synchronous
    // emissions from startImpersonation (selectedDcrSubject + currentUserSubject) into a
    // single reload and defers it to the next macrotask. That lets the sidebar's synchronous
    // permission redirect destroy this component (ngOnDestroy → unsubscribe) BEFORE the reload
    // fires, so we never issue analytics calls on behalf of an impersonated user who lacks the
    // 'analytics.overview' permission — which would 401 and log the user out instead of bouncing
    // them back to the home page like every other product does.
    this.subscriptions.add(
      merge(
        this.authService.selectedDcr$.pipe(distinctUntilChanged(), skip(1)),
        this.authService.currentUser$.pipe(map(u => u?.ID ?? null), distinctUntilChanged(), skip(1))
      )
        .pipe(debounceTime(0), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => {
          // Entity changed: drop the previous provider selection and re-scope the list.
          this.selectedProviderId = '';
          this.loadProviders();
          this.loadOverview();
          this.loadAdvanced();
        })
    );

    // Initial load only after the server-side entity sync has settled.
    this.authService.waitForEntitySync().subscribe(() => {
      this.loadProviders();
      this.loadOverview();
      this.loadAdvanced();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.providersSub?.unsubscribe();
    this.destroyAllCharts();
  }

  private get filters(): DashboardFilters {
    return { timeRange: this.selectedTimeRange, ProviderID: this.selectedProviderId || null };
  }

  get showProviderSelect(): boolean {
    return this.providerOptions.length > 0;
  }

  onProviderChange(): void {
    this.loadOverview();
    this.loadAdvanced();
  }

  // Fetch the providers, keep the active ones belonging to the selected entity, and expose
  // them as select options (prefixed with an "all" entry). The list is populated only when the
  // entity has more than one provider — otherwise the filter is pointless since the analytics
  // endpoints already aggregate at entity level. Skipped entirely in admin/global scope.
  private loadProviders(): void {
    this.providersSub?.unsubscribe();

    if (this.authService.hasGlobalDataScope() || this.authService.isTNCDataSelection()) {
      this.providerOptions = [];
      return;
    }

    const activeDcrId = this.authService.getActiveDcrId();
    const normalizedDcr = activeDcrId?.toString().trim().toLowerCase() ?? '';

    this.providersSub = this.providersApi.getProviders().subscribe({
      next: (providers: ProviderDto[]) => {
        const scoped = providers
          .filter(p => this.isActiveProvider(p))
          .filter(p => !normalizedDcr || (p.PublisherID?.toString().trim().toLowerCase() ?? '') === normalizedDcr);

        if (scoped.length <= 1) {
          this.providerOptions = [];
          return;
        }

        this.providerOptions = [
          { value: '', label: this.translate.instant('publisherDashboard.providerFilter.all') },
          ...scoped.map(p => ({ value: p.ID, label: p.Name?.trim() || p.ID }))
        ];
      },
      error: () => { this.providerOptions = []; }
    });
  }

  private isActiveProvider(provider: ProviderDto): boolean {
    if (!provider?.ID || !provider?.Name) return false;
    if (provider.Status === null || provider.Status === undefined) return true;
    if (typeof provider.Status === 'number') return provider.Status !== 0;
    const normalized = provider.Status.toString().trim().toLowerCase();
    return normalized !== '0' && normalized !== 'inactive';
  }

  selectTimeRange(days: number): void {
    if (days === this.selectedTimeRange) return;
    this.selectedTimeRange = days;
    this.loadOverview();
    this.loadAdvanced();
  }

  retryOverview(): void {
    this.loadOverview();
  }

  retryAdvanced(): void {
    this.loadAdvanced();
  }

  selectGenderView(gender: GenderView): void {
    this.selectedGenderView = gender;
    this.renderAgeBarChart();
    this.renderAgeDonutChart();
  }

  // ── Overview ────────────────────────────────────────────────────────────

  private loadOverview(): void {
    this.loadingOverview = true;
    this.overviewError = false;
    const filters = this.filters;

    forkJoin({
      active: this.api.getActiveUsers(filters),
      fresh: this.api.getNewUsers(filters),
      enriched: this.api.getEnrichedUsers(filters),
      goalTncids: this.api.getGoalsCount(filters, 'tncids'),
      goalEvents: this.api.getGoalsCount(filters, 'events'),
      emails: this.api.getEmailsCount(filters),
      maids: this.api.getMaidsCount(filters),
      trends: this.api.getTncidsTrends(filters),
      externalIds: this.api.getExternalIds(filters)
    }).pipe(
      catchError(() => of(null))
    ).subscribe(result => {
      this.loadingOverview = false;
      if (!result) {
        this.overviewError = true;
        this.setStaleDates(['activeUsers', 'newUsers', 'enrichedUsers', 'conversionEvents', 'emails', 'maids', 'trends', 'externalIds'], {});
        return;
      }

      this.overviewError = false;
      this.goalUsersCount = Number(result.goalTncids.payload) || 0;

      this.setStaleDates(['activeUsers', 'newUsers', 'enrichedUsers', 'conversionEvents', 'emails', 'maids', 'trends', 'externalIds'], {
        activeUsers: this.staleDate(result.active.dataUpdateDate),
        newUsers: this.staleDate(result.fresh.dataUpdateDate),
        enrichedUsers: this.staleDate(result.enriched.dataUpdateDate),
        conversionEvents: this.staleDate(result.goalTncids.dataUpdateDate, result.goalEvents.dataUpdateDate),
        emails: this.staleDate(result.emails.dataUpdateDate),
        maids: this.staleDate(result.maids.dataUpdateDate),
        // tncids-trends has no dataUpdateDate: the last point of the series is the data date.
        trends: this.staleDate(this.lastTrendDate(result.trends.payload)),
        externalIds: this.staleDate(result.externalIds.dataUpdateDate)
      });

      this.statCards = [
        {
          key: 'activeUsers',
          label: this.translate.instant('publisherDashboard.stats.activeUsers'),
          value: this.formatGrouped(Number(result.active.payload) || 0),
          icon: 'active'
        },
        {
          key: 'newUsers',
          label: this.translate.instant('publisherDashboard.stats.newUsers'),
          value: this.formatGrouped(Number(result.fresh.payload) || 0),
          icon: 'new'
        },
        {
          key: 'enrichedUsers',
          label: this.translate.instant('publisherDashboard.stats.enrichedUsers'),
          value: this.formatGrouped(Number(result.enriched.payload) || 0),
          icon: 'enriched'
        },
        {
          key: 'conversionEvents',
          label: this.translate.instant('publisherDashboard.stats.conversionEvents', { count: this.formatGrouped(this.goalUsersCount) }),
          value: this.formatGrouped(Number(result.goalEvents.payload) || 0),
          icon: 'events'
        },
        {
          key: 'emails',
          label: this.translate.instant('publisherDashboard.stats.emails'),
          value: this.formatGrouped(Number(result.emails.payload) || 0),
          icon: 'email'
        },
        {
          key: 'maids',
          label: this.translate.instant('publisherDashboard.stats.maids'),
          value: this.formatGrouped(Number(result.maids.payload) || 0),
          icon: 'device'
        }
      ];

      setTimeout(() => {
        this.renderTrendsChart(result.trends.payload);
        this.renderExternalIdsChart(result.externalIds.payload ?? []);
      });
    });
  }

  // ── Advanced (geo / age-gender / provider / technologies / tags) ────────

  private loadAdvanced(): void {
    this.loadingAdvanced = true;
    this.advancedError = false;
    const filters = this.filters;

    forkJoin({
      geo: this.api.getGeolocations(filters),
      sociodemos: this.api.getSociodemos(filters),
      perProvider: this.api.getTncidsPerProvider(filters),
      browser: this.api.getTechnologyBrowser(filters),
      device: this.api.getTechnologyDevice(filters),
      os: this.api.getTechnologyOs(filters),
      tags: this.api.getTncidsTags(filters)
    }).pipe(
      catchError(() => of(null))
    ).subscribe(result => {
      this.loadingAdvanced = false;
      const advancedKeys = ['geo', 'ageGender', 'providerBreakdown', 'browser', 'device', 'os', 'tags'];
      if (!result) {
        this.advancedError = true;
        this.setStaleDates(advancedKeys, {});
        return;
      }

      this.advancedError = false;

      this.setStaleDates(advancedKeys, {
        geo: this.staleDate(result.geo.dataUpdateDate),
        ageGender: this.staleDate(result.sociodemos.dataUpdateDate),
        providerBreakdown: this.staleDate(result.perProvider.dataUpdateDate),
        browser: this.staleDate(result.browser.dataUpdateDate),
        device: this.staleDate(result.device.dataUpdateDate),
        os: this.staleDate(result.os.dataUpdateDate),
        tags: this.staleDate(result.tags.dataUpdateDate)
      });

      this.geoRows = this.buildGeoRows(result.geo.payload ?? []);
      this.geoHasData = this.geoRows.length > 0;

      this.processSociodemos(result.sociodemos.payload ?? []);

      this.providerRows = this.buildTreemapRow(result.perProvider.payload ?? []);
      this.providerHasData = this.providerRows.length > 0;

      this.tagWords = this.buildTagCloud(result.tags.payload ?? []);
      this.tagsHasData = this.tagWords.length > 0;

      const browserData = result.browser.payload ?? [];
      const deviceData = result.device.payload ?? [];
      const osData = result.os.payload ?? [];
      this.browserHasData = browserData.length > 0;
      this.deviceHasData = deviceData.length > 0;
      this.osHasData = osData.length > 0;

      setTimeout(() => {
        this.renderAgeDonutChart();
        this.renderAgeBarChart();
        this.renderPieChart('browser', browserData);
        this.renderPieChart('device', deviceData);
        this.renderPieChart('os', osData);
      });
    });
  }

  // ── Chart builders ───────────────────────────────────────────────────────

  private renderTrendsChart(dto: TncidsTrendsDto | undefined): void {
    this.trendsChart?.destroy();
    this.trendsChart = null;
    const canvas = this.trendsCanvasRef?.nativeElement;
    const datasets = dto?.datasets ?? [];
    this.trendsHasData = datasets.length > 0;
    if (!canvas || !this.trendsHasData) return;

    const seriesNames = [...datasets]
      .sort((a, b) => {
        if (a.name === 'GOAL') return -1;
        if (b.name === 'GOAL') return 1;
        return a.name.localeCompare(b.name);
      })
      .map(d => d.name);

    const aggregated: Record<string, Record<string, number | string>> = {};
    datasets.forEach(ds => {
      (ds.data || []).forEach(point => {
        const day = this.toIsoDay(point.x);
        if (!day) return;
        aggregated[day] = aggregated[day] || { x: day };
        aggregated[day][ds.name] = point.y;
      });
    });

    // The x axis always covers the whole selected window up to yesterday (the API reads
    // today − timeRange → today), so days the ETL has not produced show up as empty slots
    // instead of silently shortening the chart.
    const end = new Date();
    end.setDate(end.getDate() - 1);
    const cursor = new Date(end);
    cursor.setDate(cursor.getDate() - (this.selectedTimeRange - 1));
    while (cursor <= end) {
      const day = this.localIsoDay(cursor);
      aggregated[day] = aggregated[day] || { x: day };
      cursor.setDate(cursor.getDate() + 1);
    }

    const rows = Object.values(aggregated).sort((a: any, b: any) => (a.x < b.x ? -1 : a.x === b.x ? 0 : 1));
    const hasGoal = seriesNames.includes('GOAL');

    // Contiguous runs of days with no value in any series → shaded on the chart.
    const missingRanges: [number, number][] = [];
    rows.forEach((r: any, i) => {
      const empty = seriesNames.every(n => r[n] === undefined || r[n] === null);
      if (!empty) return;
      const last = missingRanges[missingRanges.length - 1];
      if (last && last[1] === i - 1) last[1] = i; else missingRanges.push([i, i]);
    });
    const missingLabel = this.translate.instant('publisherDashboard.charts.missingData');

    const missingDataPlugin = {
      id: 'missingDataBands',
      beforeDatasetsDraw: (chart: any) => {
        if (!missingRanges.length) return;
        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        const step = rows.length > 1 ? Math.abs(xScale.getPixelForValue(1) - xScale.getPixelForValue(0)) : chartArea.width;
        ctx.save();
        missingRanges.forEach(([from, to]) => {
          const left = Math.max(chartArea.left, xScale.getPixelForValue(from) - step / 2);
          const right = Math.min(chartArea.right, xScale.getPixelForValue(to) + step / 2);
          const width = right - left;
          if (width <= 0) return;
          ctx.fillStyle = 'rgba(245, 158, 11, 0.14)';
          ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
          ctx.strokeStyle = 'rgba(217, 119, 6, 0.55)';
          ctx.setLineDash([4, 4]);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(left, chartArea.top);
          ctx.lineTo(left, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = '600 11px sans-serif';
          const textWidth = ctx.measureText(missingLabel).width;
          if (width >= textWidth + 12) {
            ctx.fillStyle = '#b45309';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(missingLabel, left + width / 2, chartArea.top + 8);
          }
        });
        ctx.restore();
      }
    };

    const chartDatasets = seriesNames.map((name, i) => {
      const isGoal = name === 'GOAL';
      const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length];
      return {
        type: isGoal ? 'bar' : 'line',
        label: isGoal ? this.translate.instant('publisherDashboard.charts.goalSeries') : name,
        data: rows.map((r: any) => r[name] ?? null),
        borderColor: color,
        backgroundColor: color + (isGoal ? 'AA' : '22'),
        borderWidth: isGoal ? 0 : 2,
        tension: 0.35,
        // A value with no neighbours (e.g. the day after a gap) draws no line segment:
        // show it as a dot, otherwise it would be invisible.
        pointRadius: (ctx: any) => {
          const data = ctx.dataset.data as (number | null)[];
          const i = ctx.dataIndex;
          const isolated = data[i] != null && data[i - 1] == null && data[i + 1] == null;
          return isolated ? 3 : 0;
        },
        pointBackgroundColor: color,
        fill: !isGoal,
        yAxisID: isGoal ? 'yGoal' : 'y',
        borderRadius: isGoal ? 4 : 0
      } as any;
    });

    const config: ChartConfiguration = {
      type: 'bar',
      plugins: [missingDataPlugin as any],
      data: {
        labels: rows.map((r: any) => this.formatShortDate(r.x)),
        datasets: chartDatasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${(ctx.parsed as any).y?.toLocaleString() ?? ''}`
            }
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 10 } },
          y: { position: 'left', grid: { color: '#eef1f6' }, ticks: { callback: (v: any) => Number(v).toLocaleString() } },
          yGoal: {
            position: 'right',
            display: hasGoal,
            grid: { drawOnChartArea: false },
            ticks: { callback: (v: any) => Number(v).toLocaleString() }
          } as any
        }
      }
    };

    this.trendsChart = new Chart(canvas, config);
  }

  private renderExternalIdsChart(data: { Name: string; Count: number }[]): void {
    this.externalIdsChart?.destroy();
    this.externalIdsChart = null;
    this.externalIdsHasData = (data || []).length > 0;
    const canvas = this.externalIdsCanvasRef?.nativeElement;
    if (!canvas || !this.externalIdsHasData) return;

    const sorted = [...data].sort((a, b) => b.Count - a.Count);
    const colors = sorted.map((_, i) => MONO_BLUE_SHADES[i % MONO_BLUE_SHADES.length]);
    this.externalIdsChart = new Chart(canvas, this.simpleBarConfig(sorted.map(d => d.Name), sorted.map(d => d.Count), colors));
  }

  private simpleBarConfig(labels: string[], values: number[], color: string | string[]): ChartConfiguration {
    const fill = Array.isArray(color) ? color.map(c => c + 'CC') : color + 'CC';
    const hoverFill = color;
    return {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: fill,
          hoverBackgroundColor: hoverFill,
          borderRadius: 4,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${Number(ctx.parsed.y).toLocaleString()}` } }
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 30 } },
          y: { grid: { color: '#eef1f6' }, ticks: { callback: (v: any) => Number(v).toLocaleString() } }
        }
      }
    };
  }

  private processSociodemos(data: SociodemoRowDto[]): void {
    const totalsRow = data.find(d => d.Label === 'null');
    const categoryRows = data.filter(d => d.Label !== 'null');

    if (!totalsRow || !categoryRows.length) {
      this.ageGenderHasData = false;
      this.maleBreakdown = [];
      this.femaleBreakdown = [];
      this.totalBreakdown = [];
      this.malePercent = 0;
      this.femalePercent = 0;
      return;
    }

    this.ageGenderHasData = true;
    const knownTotal = totalsRow.Total - totalsRow.Unknown;
    this.malePercent = knownTotal > 0 ? (totalsRow.Male / knownTotal) * 100 : 0;
    this.femalePercent = knownTotal > 0 ? (totalsRow.Female / knownTotal) * 100 : 0;

    this.maleBreakdown = categoryRows.map(r => ({
      label: r.Label,
      count: r.Male,
      percent: totalsRow.Male ? (r.Male / totalsRow.Male) * 100 : 0
    }));
    this.femaleBreakdown = categoryRows.map(r => ({
      label: r.Label,
      count: r.Female,
      percent: totalsRow.Female ? (r.Female / totalsRow.Female) * 100 : 0
    }));
    this.totalBreakdown = categoryRows.map(r => ({
      label: r.Label,
      count: r.Total,
      percent: totalsRow.Total ? (r.Total / totalsRow.Total) * 100 : 0
    }));

    this.selectedGenderView = 'total';
  }

  private renderAgeDonutChart(): void {
    this.ageDonutChart?.destroy();
    this.ageDonutChart = null;
    const canvas = this.ageDonutCanvasRef?.nativeElement;
    if (!canvas || !this.ageGenderHasData) return;

    this.ageDonutChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: [
          this.translate.instant('publisherDashboard.ageGender.male'),
          this.translate.instant('publisherDashboard.ageGender.female')
        ],
        datasets: [{
          data: [this.malePercent, this.femalePercent],
          backgroundColor: [CATEGORY_COLORS[0] + 'CC', CATEGORY_COLORS[5] + 'CC'],
          hoverBackgroundColor: [CATEGORY_COLORS[0], CATEGORY_COLORS[5]],
          offset: [
            this.selectedGenderView === 'male' ? 10 : 0,
            this.selectedGenderView === 'female' ? 10 : 0
          ],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${Number(ctx.parsed).toFixed(1)}%` } }
        }
      }
    });
  }

  private renderAgeBarChart(): void {
    this.ageBarChart?.destroy();
    this.ageBarChart = null;
    const canvas = this.ageBarCanvasRef?.nativeElement;
    if (!canvas || !this.ageGenderHasData) return;

    const source = this.selectedGenderView === 'male' ? this.maleBreakdown
      : this.selectedGenderView === 'female' ? this.femaleBreakdown
      : this.totalBreakdown;
    const color = this.selectedGenderView === 'male' ? CATEGORY_COLORS[0]
      : this.selectedGenderView === 'female' ? CATEGORY_COLORS[5]
      : CATEGORY_COLORS[1];

    this.ageBarChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: source.map(d => d.label),
        datasets: [{
          data: source.map(d => d.percent),
          backgroundColor: color + 'CC',
          hoverBackgroundColor: color,
          borderRadius: 4,
          borderSkipped: false
        } as any]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const count = source[ctx.dataIndex]?.count ?? 0;
                return ` ${Number(ctx.parsed.x).toFixed(1)}% (${count.toLocaleString()})`;
              }
            }
          }
        },
        scales: {
          x: { display: false, min: 0, max: 100 },
          y: { grid: { display: false }, ticks: { font: { size: 11 } } }
        }
      }
    });
  }

  private renderPieChart(kind: 'browser' | 'device' | 'os', data: CategoryValueDto[]): void {
    const canvasRef = kind === 'browser' ? this.browserPieCanvasRef
      : kind === 'device' ? this.devicePieCanvasRef
      : this.osPieCanvasRef;

    const existing = kind === 'browser' ? this.browserPieChart
      : kind === 'device' ? this.devicePieChart
      : this.osPieChart;
    existing?.destroy();

    const hasData = (data || []).length > 0;
    if (kind === 'browser') this.browserHasData = hasData;
    if (kind === 'device') this.deviceHasData = hasData;
    if (kind === 'os') this.osHasData = hasData;

    const canvas = canvasRef?.nativeElement;
    if (!canvas || !hasData) {
      if (kind === 'browser') this.browserPieChart = null;
      if (kind === 'device') this.devicePieChart = null;
      if (kind === 'os') this.osPieChart = null;
      return;
    }

    const sorted = [...data].sort((a, b) => b.value - a.value);
    const maxItems = 6;
    const top = sorted.slice(0, maxItems);
    const rest = sorted.slice(maxItems);
    const combined = rest.length
      ? [...top, { category: this.translate.instant('publisherDashboard.technologies.other'), value: rest.reduce((s, r) => s + r.value, 0) }]
      : top;

    const chart = new Chart(canvas, {
      type: 'pie',
      data: {
        labels: combined.map(d => d.category),
        datasets: [{
          data: combined.map(d => d.value),
          backgroundColor: CATEGORY_COLORS.slice(0, combined.length),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: {
              padding: 10,
              boxWidth: 12,
              generateLabels: (chartInstance) => {
                const values = (chartInstance.data.datasets[0]?.data as number[]) ?? [];
                const total = values.reduce((a, b) => a + b, 0);
                return (chartInstance.data.labels as string[]).map((label, i) => ({
                  text: `${label}: ${total ? (values[i] / total * 100).toFixed(1) : 0}%`,
                  fillStyle: (chartInstance.data.datasets[0].backgroundColor as string[])[i],
                  strokeStyle: 'transparent',
                  hidden: false,
                  index: i
                }));
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const values = ctx.dataset.data as number[];
                const total = values.reduce((a, b) => a + b, 0);
                const pct = total ? (Number(ctx.parsed) / total * 100).toFixed(1) : '0';
                return ` ${ctx.label}: ${Number(ctx.parsed).toLocaleString()} (${pct}%)`;
              }
            }
          }
        }
      }
    });

    if (kind === 'browser') this.browserPieChart = chart;
    if (kind === 'device') this.devicePieChart = chart;
    if (kind === 'os') this.osPieChart = chart;
  }

  // ── Treemap row (provider breakdown): one rectangle, divided vertically ─
  // into columns sized by value share, shaded light → dark.

  private buildTreemapRow(data: XyPointDto[], maxItems = 6): RankedRow[] {
    if (!data || !data.length) return [];
    const sorted = [...data].sort((a, b) => b.y - a.y);
    const top = sorted.slice(0, maxItems);
    const rest = sorted.slice(maxItems);
    const combined = rest.length
      ? [...top, { x: this.translate.instant('publisherDashboard.technologies.other'), y: rest.reduce((s, r) => s + r.y, 0) }]
      : top;

    const total = combined.reduce((s, i) => s + i.y, 0);
    return combined.map((item, i) => ({
      label: item.x,
      value: item.y,
      pct: total ? Math.round((item.y / total) * 100) : 0,
      color: rest.length && i === combined.length - 1 ? '#94a3b8' : MONO_BLUE_SHADES[i % MONO_BLUE_SHADES.length]
    }));
  }

  // ── Tag cloud: font size scales with how often each tag occurs ─────────

  private buildTagCloud(data: XyPointDto[], maxWords = 60): TagCloudWord[] {
    if (!data || !data.length) return [];
    const sorted = [...data].sort((a, b) => b.y - a.y).slice(0, maxWords);
    const total = sorted.reduce((s, i) => s + i.y, 0);
    const values = sorted.map(d => d.y);
    const min = Math.min(...values);
    const max = Math.max(...values);

    const MIN_FONT = 13;
    const MAX_FONT = 42;

    return sorted.map((item, i) => {
      const ratio = max > min ? Math.sqrt((item.y - min) / (max - min)) : 1;
      return {
        label: item.x,
        value: item.y,
        pct: total ? Math.round((item.y / total) * 100) : 0,
        fontSize: Math.round(MIN_FONT + ratio * (MAX_FONT - MIN_FONT)),
        color: CATEGORY_COLORS[i % CATEGORY_COLORS.length]
      };
    });
  }

  // ── Geo ───────────────────────────────────────────────────────────────

  private buildGeoRows(data: GeoVolumeDto[]): GeoRow[] {
    const byCountry = new Map<string, number>();
    (data || []).forEach(row => {
      const key = row.CountryCode;
      if (!key) return;
      byCountry.set(key, (byCountry.get(key) || 0) + (Number(row.TNCIDs) || 0));
    });

    const rows = Array.from(byCountry.entries())
      .map(([code, value]) => ({ code, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const total = rows.reduce((s, r) => s + r.value, 0) || 1;
    return rows.map(r => ({
      code: r.code,
      name: this.regionNames?.of(r.code) ?? r.code,
      value: r.value,
      pct: Math.round((r.value / total) * 100)
    }));
  }

  private createRegionDisplayNames(): { of(code: string): string } | null {
    try {
      const DisplayNamesCtor = (Intl as any).DisplayNames;
      if (!DisplayNamesCtor) return null;
      return new DisplayNamesCtor(['en'], { type: 'region' });
    } catch {
      return null;
    }
  }

  // ── Data freshness ───────────────────────────────────────────────────────

  // Replaces the stale dates of the given cards (keys missing or null in `dates` become fresh).
  private setStaleDates(keys: string[], dates: Record<string, string | null>): void {
    const next = { ...this.staleDates };
    keys.forEach(k => {
      const d = dates[k];
      if (d) next[k] = d; else delete next[k];
    });
    this.staleDates = next;
  }

  // Oldest of the given data dates if it is before yesterday (local time), else null.
  // Unknown/empty dates are ignored, so a card without a date never shows a warning.
  private staleDate(...dates: (string | null | undefined)[]): string | null {
    const valid = dates
      .map(d => (typeof d === 'string' ? d.slice(0, 10) : ''))
      .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort();
    if (!valid.length) return null;
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const yesterday = this.localIsoDay(y);
    return valid[0] < yesterday ? valid[0] : null;
  }

  private lastTrendDate(dto: TncidsTrendsDto | undefined): string | null {
    let last: string | null = null;
    (dto?.datasets ?? []).forEach(ds => (ds.data || []).forEach(p => {
      const d = this.toIsoDay(p.x);
      if (d && (!last || d > last)) last = d;
    }));
    return last;
  }

  // Normalizes a trend x value ('YYYY-MM-DD…' or { value }) to 'YYYY-MM-DD'; '' if unparseable.
  private toIsoDay(x: unknown): string {
    const raw = typeof x === 'string' ? x : (x as any)?.value;
    const d = typeof raw === 'string' ? raw.slice(0, 10) : '';
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
  }

  private localIsoDay(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  formatDataDate(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return iso;
    return date.toLocaleDateString(this.locale, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // ── Formatting helpers ───────────────────────────────────────────────────

  formatValue(v: number): string {
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(1) + 'K';
    return Math.round(v).toLocaleString();
  }

  private formatGrouped(n: number): string {
    try {
      return new Intl.NumberFormat(this.locale).format(n);
    } catch {
      return String(n);
    }
  }

  private formatShortDate(value: { value: string } | string): string {
    const raw = typeof value === 'string' ? value : value?.value;
    const date = new Date(raw);
    if (isNaN(date.getTime())) return String(raw ?? '');
    return date.toLocaleDateString(this.locale, { day: 'numeric', month: 'short' });
  }

  private destroyAllCharts(): void {
    this.trendsChart?.destroy();
    this.externalIdsChart?.destroy();
    this.ageDonutChart?.destroy();
    this.ageBarChart?.destroy();
    this.browserPieChart?.destroy();
    this.devicePieChart?.destroy();
    this.osPieChart?.destroy();
  }
}
